import { EmailThreadView } from "components/EmailThreadView";
import { PortalActionMenu } from "components/PortalActionMenu";
import { Badge } from "components/ui/Badge/Badge";
import { Button } from "components/ui/Button";
import {
  type ReplyEmailItem,
  type ReplyResponseStatus,
  replyStatusBadgeColor,
  replyStatusLabel,
} from "hooks/use-email-replies-api";
import {
  Briefcase,
  ChevronDown,
  ExternalLink,
  Link as LinkIcon,
  Megaphone,
  Reply,
  Send,
  User as UserIcon,
} from "lucide-react";
import { type FC, useMemo } from "react";
import { ReplyParentPill } from "routes/emails/EmailRepliesPage/components/ReplyParentPill";
import { buildStatusMenuItems } from "routes/emails/EmailRepliesPage/lib/build-status-menu-items";
import { formatShortDateTime } from "utils/relative-time";

import { InlineReplyComposer } from "./InlineReplyComposer";

export type ReplyThreadPaneProps = {
  reply: ReplyEmailItem | null;
  /** Gmail 連携がある場合 true. false なら返信送信を disabled にする */
  canCompose: boolean;
  onSetStatus: (replyId: string, status: ReplyResponseStatus) => Promise<void>;
  onStartReply: () => void;
  onOpenCrmLink: () => void;
  /** 返信送信成功時に呼ばれる. 一覧 refetch 等を行う */
  onReplySent: () => void;
};

/**
 * 右ペイン. ステータス + メタ情報 + スレッドインライン表示 + アクション + インライン返信ペイン.
 * @param {ReplyThreadPaneProps} props 選択リプライとハンドラ
 * @return {JSX.Element} 右ペイン
 */
export const ReplyThreadPane: FC<ReplyThreadPaneProps> = ({
  reply,
  canCompose,
  onSetStatus,
  onStartReply,
  onOpenCrmLink,
  onReplySent,
}) => {
  if (!reply) {
    return (
      <div className="flex-1 bg-white rounded-lg border border-slate-200 p-4 flex items-center justify-center text-sm text-base-content/60">
        返信を選択してください
      </div>
    );
  }

  return (
    <div className="flex-1 bg-white rounded-lg border border-slate-200 flex flex-col min-h-0 overflow-hidden">
      <ReplyStatusBar reply={reply} onSetStatus={onSetStatus} />
      <ReplyMetaSection reply={reply} />
      <div className="flex-1 overflow-auto px-4 py-3 space-y-4 min-h-0">
        <ReplyThreadSection reply={reply} />
      </div>
      <ReplyActionSection
        reply={reply}
        canCompose={canCompose}
        onStartReply={onStartReply}
        onOpenCrmLink={onOpenCrmLink}
        onReplySent={onReplySent}
      />
    </div>
  );
};

// ============================================================
// Sub-sections
// ============================================================

type ReplyStatusBarProps = {
  reply: ReplyEmailItem;
  onSetStatus: (replyId: string, status: ReplyResponseStatus) => Promise<void>;
};

/**
 * 件名より上に置くステータス切替バー (Badge ＋ Dropdown).
 * @param {ReplyStatusBarProps} props 選択リプライとハンドラ
 * @return {JSX.Element} ステータスバー
 */
const ReplyStatusBar: FC<ReplyStatusBarProps> = ({ reply, onSetStatus }) => {
  const menuItems = useMemo(
    () =>
      buildStatusMenuItems(reply.responseStatus, status =>
        onSetStatus(reply.id, status),
      ),
    [reply.id, reply.responseStatus, onSetStatus],
  );

  return (
    <div className="px-4 pt-3 pb-2 border-b border-slate-100 flex items-center gap-2">
      <span className="text-xs text-base-content/60">ステータス</span>
      <PortalActionMenu
        items={menuItems}
        buttonLabel={
          <span className="inline-flex items-center gap-1">
            <Badge
              color={replyStatusBadgeColor(reply.responseStatus)}
              size="sm"
            >
              {replyStatusLabel(reply.responseStatus)}
            </Badge>
            <ChevronDown size={14} aria-hidden />
          </span>
        }
      />
    </div>
  );
};

type ReplyMetaSectionProps = {
  reply: ReplyEmailItem;
};

/**
 * 件名・送信元・受信時刻・関連 CRM 情報を表示するメタヘッダ.
 * @param {ReplyMetaSectionProps} props 選択リプライ
 * @return {JSX.Element} メタブロック
 */
const ReplyMetaSection: FC<ReplyMetaSectionProps> = ({ reply }) => {
  return (
    <div className="px-4 py-3 border-b border-slate-200">
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-base-content/60">件名</div>
          <div className="text-sm font-semibold break-words">
            {reply.subject || "(件名なし)"}
          </div>
        </div>
        <ReplyParentPill type={reply.parentType} />
      </div>
      <div className="mt-2 flex items-baseline gap-3 text-sm">
        <span className="text-xs text-base-content/60 shrink-0">送信元</span>
        <span className="font-medium truncate">
          {reply.fromName ? `${reply.fromName} ` : ""}
          <span className="text-base-content/60">
            &lt;{reply.fromEmail}&gt;
          </span>
        </span>
      </div>
      <div className="mt-1 text-xs text-base-content/50">
        受信: {formatShortDateTime(reply.receivedAt)}
      </div>
      <ReplyContextChips reply={reply} />
    </div>
  );
};

type ReplyContextChipsProps = {
  reply: ReplyEmailItem;
};

/**
 * 関連 CRM / 配信情報のチップ列.
 * @param {ReplyContextChipsProps} props 選択リプライ
 * @return {JSX.Element|null} チップ列 (情報が無いときは null)
 */
const ReplyContextChips: FC<ReplyContextChipsProps> = ({ reply }) => {
  const hasContext =
    reply.pipelineTalentName ||
    reply.pipelineMatterTitle ||
    reply.deliverySubject ||
    reply.sentEmailSubject;
  if (!hasContext) return null;
  return (
    <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-violet-700">
      {reply.pipelineTalentName && (
        <span className="flex items-center gap-1">
          <UserIcon size={12} aria-hidden />
          {reply.pipelineTalentName}
        </span>
      )}
      {reply.pipelineMatterTitle && (
        <span className="flex items-center gap-1">
          <Briefcase size={12} aria-hidden />
          {reply.pipelineMatterTitle}
        </span>
      )}
      {reply.deliverySubject && (
        <span className="flex items-center gap-1">
          <Megaphone size={12} aria-hidden />
          配信: {reply.deliverySubject}
        </span>
      )}
      {reply.seriesName && reply.seriesId && (
        <a
          href={`/delivery-series/${reply.seriesId}`}
          target="_blank"
          rel="noreferrer"
          className="flex items-center gap-1 hover:underline"
        >
          <Megaphone size={12} aria-hidden />
          シリーズ: {reply.seriesName}
        </a>
      )}
      {reply.sentEmailSubject && (
        <span className="flex items-center gap-1">
          <Send size={12} aria-hidden />
          送信: {reply.sentEmailSubject}
        </span>
      )}
    </div>
  );
};

type ReplyThreadSectionProps = {
  reply: ReplyEmailItem;
};

/**
 * スレッドビューのインライン表示. providerThreadId が無い場合は本文 fallback.
 * @param {ReplyThreadSectionProps} props 選択リプライ
 * @return {JSX.Element} スレッドブロック or 本文 fallback
 */
const ReplyThreadSection: FC<ReplyThreadSectionProps> = ({ reply }) => {
  if (!reply.providerThreadId) {
    return (
      <div>
        <div className="text-xs text-base-content/60 mb-1">本文</div>
        <pre className="text-xs whitespace-pre-wrap break-words bg-slate-50 rounded p-3 border border-slate-200">
          {reply.bodyText || "(本文なし)"}
        </pre>
        <div className="mt-2 text-[11px] text-base-content/50">
          ※ このリプライにはスレッド情報が紐付いていません
        </div>
      </div>
    );
  }
  return <EmailThreadView providerThreadId={reply.providerThreadId} />;
};

type ReplyActionSectionProps = {
  reply: ReplyEmailItem;
  canCompose: boolean;
  onStartReply: () => void;
  onOpenCrmLink: () => void;
  onReplySent: () => void;
};

/**
 * インライン返信入力 + サブアクション (CRM 連携 / 元メール詳細).
 * 旧「対応メモ」 は廃止。`InlineReplyComposer` がここに埋め込まれる.
 * @param {ReplyActionSectionProps} props 状態とハンドラ
 * @return {JSX.Element} アクションブロック
 */
const ReplyActionSection: FC<ReplyActionSectionProps> = ({
  reply,
  canCompose,
  onStartReply,
  onOpenCrmLink,
  onReplySent,
}) => {
  return (
    <div className="border-t border-slate-200 px-4 py-3 space-y-3">
      <InlineReplyComposer
        reply={reply}
        canCompose={canCompose}
        onOpenFullEditor={onStartReply}
        onSent={onReplySent}
      />
      <div className="flex gap-2 flex-wrap pt-1 border-t border-slate-100">
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={onOpenCrmLink}
        >
          <span className="inline-flex items-center gap-1">
            <LinkIcon size={12} aria-hidden />
            CRM 連携
          </span>
        </Button>
        <Button
          type="button"
          size="xs"
          variant="outline"
          onClick={onStartReply}
          disabled={!canCompose}
          title={canCompose ? undefined : "返信送信には Gmail 連携が必要です"}
        >
          <span className="inline-flex items-center gap-1">
            <Reply size={12} aria-hidden />
            フル機能で返信
          </span>
        </Button>
        {reply.deliveryEmailId && (
          <Button type="button" size="xs" variant="outline" asChild>
            <a
              href={`/deliveries/${reply.deliveryEmailId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1"
            >
              <ExternalLink size={12} aria-hidden />
              元メール詳細
            </a>
          </Button>
        )}
      </div>
    </div>
  );
};
